import cloneDeep from "lodash/cloneDeep";

export interface SensitiveFieldInfo {
  key: string;
  description: string;
  secret: boolean;
  report?: boolean;
  getDisplayName: (object: Record<string, unknown>, key: string, value: unknown) => string;
}

export interface SanitizedFieldInfo {
  key: string;
  description: string;
  secret: boolean;
  displayName: string;
  value?: unknown;
}

const getConnectionDisplayName = (object: Record<string, unknown>, key: string) => {
  const connectionName = typeof object.name === "string" ? object.name : "Connection";
  return `${connectionName} — ${key}`;
};

const getFileProcessingDisplayName = (_object: Record<string, unknown>, key: string) => `File processing — ${key}`;

const getAdapterDisplayName = (object: Record<string, unknown>, key: string) => {
  const adapterName = typeof object.id === "string" ? object.id : "Adapter";
  return `${adapterName} — ${key}`;
};

export const SENSITIVE_CONNECTION_FIELDS: SensitiveFieldInfo[] = [
  {
    key: "url",
    description: "Connection endpoint URL. Configure the endpoint URL for this environment in the connection settings.",
    secret: false,
    getDisplayName: getConnectionDisplayName,
  },
  {
    key: "host",
    description: "Connection host. Configure the host for this environment in the connection settings.",
    secret: false,
    getDisplayName: getConnectionDisplayName,
  },
  {
    key: "username",
    description: "Connection username. Enter this environment's username in the connection settings.",
    secret: false,
    getDisplayName: getConnectionDisplayName,
  },
  {
    key: "password",
    description: "Connection password. Enter this environment's password in the connection settings.",
    secret: true,
    getDisplayName: getConnectionDisplayName,
  },
  {
    key: "apiKey",
    description: "API key. Enter an API key scoped to this environment in the connection settings.",
    secret: true,
    getDisplayName: getConnectionDisplayName,
  },
  {
    key: "jwt",
    description: "JSON Web Token. Enter a token scoped to this environment in the connection settings.",
    secret: true,
    getDisplayName: getConnectionDisplayName,
  },
  {
    key: "oauth.clientSecret",
    description: "OAuth client secret. Enter this environment's client secret in the connection settings.",
    secret: true,
    getDisplayName: getConnectionDisplayName,
  },
  {
    key: "oauth.tokenUrl",
    description:
      "OAuth token endpoint URL. Configure the token endpoint for this environment in the connection settings.",
    secret: false,
    getDisplayName: getConnectionDisplayName,
  },
];

export const SENSITIVE_MODEL_FIELDS: SensitiveFieldInfo[] = [
  {
    key: "fileProcessing.username",
    description: "File-processing username. Configure the username for this environment in file processing settings.",
    secret: false,
    getDisplayName: getFileProcessingDisplayName,
  },
  {
    key: "fileProcessing.sshKeys",
    description:
      "File-processing SSH public key. Generate or upload a key for this environment in file processing settings.",
    secret: true,
    getDisplayName: getFileProcessingDisplayName,
  },
  {
    key: "fileProcessing.sftpHost",
    description: "File-processing SFTP host. Configure the SFTP host for this environment in file processing settings.",
    secret: false,
    getDisplayName: getFileProcessingDisplayName,
  },
];

const SENSITIVE_ADAPTER_FIELDS: SensitiveFieldInfo[] = [
  {
    key: "adapters.*.answers.baseUrl",
    description: "Adapter base URL. Configure the base URL for this environment in the adapter settings.",
    secret: false,
    report: false,
    getDisplayName: getAdapterDisplayName,
  },
  {
    key: "adapters.*.answers.username",
    description: "Adapter username. Enter this environment's username in the adapter settings.",
    secret: false,
    report: false,
    getDisplayName: getAdapterDisplayName,
  },
  {
    key: "adapters.*.answers.password",
    description: "Adapter password. Enter this environment's password in the adapter settings.",
    secret: true,
    report: false,
    getDisplayName: getAdapterDisplayName,
  },
  {
    key: "adapters.*.answers.apiKey",
    description: "Adapter API key. Enter an API key scoped to this environment in the adapter settings.",
    secret: true,
    report: false,
    getDisplayName: getAdapterDisplayName,
  },
];

const connectionPaths = (prefix: string) =>
  SENSITIVE_CONNECTION_FIELDS.map((field) => ({ ...field, key: `${prefix}.${field.key}` }));
const RELEASE_ENVIRONMENT_CONFIGURATION_PATHS = [...connectionPaths("connections.*"), ...SENSITIVE_ADAPTER_FIELDS];
const CONNECTION_DOCUMENT_ENVIRONMENT_CONFIGURATION_PATHS = [
  ...connectionPaths("contents"),
  ...connectionPaths("contents.*"),
];

const removePath = (
  value: any,
  path: string[],
  sensitiveField: SensitiveFieldInfo,
  sanitizedFields: Map<string, SanitizedFieldInfo>,
  displayObject: Record<string, unknown>,
) => {
  if (!value || typeof value !== "object" || path.length === 0) return;

  const [segment, ...remainingPath] = path;
  const keys = segment === "*" ? Object.keys(value) : [segment];

  for (const key of keys) {
    if (!(key in value)) continue;
    if (remainingPath.length === 0) {
      const currentValue = value[key];
      delete value[key];
      if (sensitiveField.report === false) continue;
      sanitizedFields.set(sensitiveField.key, {
        key: sensitiveField.key,
        description: sensitiveField.description,
        secret: sensitiveField.secret,
        displayName: sensitiveField.getDisplayName(displayObject, path.join("."), currentValue),
      });
    } else {
      const nextDisplayObject =
        segment === "*" && value[key] && typeof value[key] === "object" ? value[key] : displayObject;
      removePath(value[key], remainingPath, sensitiveField, sanitizedFields, nextDisplayObject);
    }
  }
};

const stripPaths = <T extends object>(value: T, fields: SensitiveFieldInfo[], pathPrefix = "") => {
  const sanitized = cloneDeep(value) as T;
  const sanitizedFields = new Map<string, SanitizedFieldInfo>();

  for (const field of fields) {
    const sanitizedField = { ...field, key: `${pathPrefix}${field.key}` };
    removePath(sanitized, field.key.split("."), sanitizedField, sanitizedFields, sanitized as Record<string, unknown>);
  }

  return { value: sanitized, sanitizedFields };
};

export const sanitizeReleaseExport = <TRelease extends object, TDocument extends Record<string, unknown>>(
  release: TRelease,
  documents: TDocument[] = [],
): { release: TRelease; documents: TDocument[]; sanitizedFields: SanitizedFieldInfo[] } => {
  const sanitizedRelease = stripPaths(release, RELEASE_ENVIRONMENT_CONFIGURATION_PATHS);
  const sanitizedFields = new Map(sanitizedRelease.sanitizedFields);
  const sanitizedDocuments = documents.map((document) => {
    if (document.type !== "connection") return cloneDeep(document);

    const sanitizedDocument = stripPaths(document, CONNECTION_DOCUMENT_ENVIRONMENT_CONFIGURATION_PATHS, "documents.*.");
    for (const [key, field] of sanitizedDocument.sanitizedFields) sanitizedFields.set(key, field);
    return sanitizedDocument.value;
  });

  return {
    release: sanitizedRelease.value,
    documents: sanitizedDocuments,
    sanitizedFields: [...sanitizedFields.values()],
  };
};

export const sanitizeProjectExport = <TModel extends object>(
  model: TModel,
): { model: TModel; sanitizedFields: SanitizedFieldInfo[] } => {
  const sanitizedModel = stripPaths(model, SENSITIVE_MODEL_FIELDS, "model.");
  return { model: sanitizedModel.value, sanitizedFields: [...sanitizedModel.sanitizedFields.values()] };
};

export const getSanitizedFields = (exportPayload: any): SanitizedFieldInfo[] => {
  if (
    Array.isArray(exportPayload.sanitizedFields) &&
    exportPayload.sanitizedFields.every(
      (field: any) =>
        typeof field?.key === "string" &&
        typeof field?.description === "string" &&
        typeof field?.displayName === "string",
    )
  ) {
    return exportPayload.sanitizedFields;
  }
  return sanitizeReleaseExport(exportPayload.release, exportPayload.documents || []).sanitizedFields;
};
